<script>
const DAYS = [1, 2, 3, 4, 5, 6, 7];
const SHIFTS = ["manha", "tarde", "noite"];

export default {
  name: "ProfessionalWorkingHoursForm",
  props: {
    workingHours: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    showSaveButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localHours: this.buildInitialState(),
      requiredFlags: this.buildRequiredFlags(),
    };
  },
  watch: {
    workingHours: {
      handler() {
        this.localHours = this.buildInitialState();
        this.requiredFlags = this.buildRequiredFlags();
      },
      deep: true,
    },
  },
  computed: {
    dayOptions() {
      return DAYS.map((day) => ({
        value: day,
        label: this.$t(`mesages.professional.working_hours.days.${day}`),
      }));
    },
    shiftLabels() {
      return SHIFTS.reduce((labels, shift) => {
        labels[shift] = this.$t(`mesages.professional.working_hours.shifts.${shift}`);
        return labels;
      }, {});
    },
  },
  methods: {
    buildInitialState() {
      const grouped = this.groupHoursByDay();
      return DAYS.map((day) => ({
        dayOfWeek: day,
        shifts: SHIFTS.map((shift) => ({
          shift,
          start_time: grouped[day]?.[shift]?.start_time?.slice(0, 5) || "",
          end_time: grouped[day]?.[shift]?.end_time?.slice(0, 5) || "",
        })),
      }));
    },
    buildRequiredFlags() {
      return DAYS.map((day) => ({
        dayOfWeek: day,
        shifts: SHIFTS.map((shift) => ({
          shift,
          required: false,
        })),
      }));
    },
    groupHoursByDay() {
      return this.workingHours.reduce((acc, hour) => {
        const day = hour.day_of_week || hour.dayOfWeek;
        if (!acc[day]) {
          acc[day] = {};
        }
        acc[day][hour.shift] = hour;
        return acc;
      }, {});
    },
    updateTime(dayIndex, shiftIndex, field, value) {
      this.localHours = this.localHours.map((day, currentDayIndex) => {
        if (currentDayIndex !== dayIndex) {
          return day;
        }

        return {
          ...day,
          shifts: day.shifts.map((shift, currentShiftIndex) => {
            if (currentShiftIndex !== shiftIndex) {
              return shift;
            }

            const nextShift = {
              ...shift,
              [field]: value,
            };

            const hasAnyValue = [nextShift.start_time, nextShift.end_time].some(
              (entry) => entry !== null && entry !== ""
            );

            this.requiredFlags = this.requiredFlags.map((flagDay, flagDayIndex) => {
              if (flagDayIndex !== dayIndex) {
                return flagDay;
              }

              return {
                ...flagDay,
                shifts: flagDay.shifts.map((flagShift, flagShiftIndex) => {
                  if (flagShiftIndex !== shiftIndex) {
                    return flagShift;
                  }

                  return {
                    ...flagShift,
                    required: hasAnyValue,
                  };
                }),
              };
            });

            return nextShift;
          }),
        };
      });
    },
    buildPayload() {
      const payload = [];
      this.localHours.forEach((day) => {
        day.shifts.forEach((shift) => {
          if (!shift.start_time || !shift.end_time) {
            return;
          }

          payload.push({
            day_of_week: day.dayOfWeek,
            shift: shift.shift,
            start_time: shift.start_time,
            end_time: shift.end_time,
          });
        });
      });
      return payload;
    },
    onSubmit() {
      const payload = this.buildPayload();
      this.$emit("save", payload);
    },
  },
};
</script>

<template>
  <div>
    <div v-if="loading" class="text-center py-3">
      <b-spinner label="Loading"></b-spinner>
      <p class="mt-2 mb-0 text-muted">
        {{ $t("mesages.professional.working_hours.loading") }}
      </p>
    </div>
    <div v-else>
      <div class="table-responsive">
        <p class="text-muted small mb-2">
          {{ $t("mesages.professional.working_hours.optional_hint") }}
        </p>
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th style="width: 15%">{{ $t("mesages.professional.working_hours.day") }}</th>
              <th style="width: 85%" colspan="3">{{ $t("mesages.professional.working_hours.shift") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(day, dayIndex) in localHours" :key="day.dayOfWeek">
              <td class="align-middle font-weight-semibold">
                {{ $t(`mesages.professional.working_hours.days.${day.dayOfWeek}`) }}
              </td>
              <td
                v-for="(shift, shiftIndex) in day.shifts"
                :key="shift.shift"
                class="align-middle"
              >
                <div class="form-row">
                  <div class="col-12">
                    <strong>{{ shiftLabels[shift.shift] }}</strong>
                  </div>
                  <div class="col">
                    <label class="sr-only" :for="`start-${dayIndex}-${shiftIndex}`">
                      {{ $t("mesages.professional.working_hours.start") }}
                    </label>
                    <input
                      :id="`start-${dayIndex}-${shiftIndex}`"
                      type="time"
                      class="form-control form-control-sm"
                      :class="{ 'is-invalid': requiredFlags[dayIndex].shifts[shiftIndex].required && !shift.start_time }"
                      :value="shift.start_time"
                      @input="updateTime(dayIndex, shiftIndex, 'start_time', $event.target.value)"
                    />
                  </div>
                  <div class="col">
                    <label class="sr-only" :for="`end-${dayIndex}-${shiftIndex}`">
                      {{ $t("mesages.professional.working_hours.end") }}
                    </label>
                    <input
                      :id="`end-${dayIndex}-${shiftIndex}`"
                      type="time"
                      class="form-control form-control-sm"
                      :class="{ 'is-invalid': requiredFlags[dayIndex].shifts[shiftIndex].required && !shift.end_time }"
                      :value="shift.end_time"
                      @input="updateTime(dayIndex, shiftIndex, 'end_time', $event.target.value)"
                    />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="showSaveButton" class="text-right">
        <b-button
          variant="primary"
          :disabled="saving"
          @click="onSubmit"
        >
          <b-spinner
            v-if="saving"
            small
            type="border"
            class="mr-2"
          ></b-spinner>
          {{ $t("mesages.professional.working_hours.save") }}
        </b-button>
      </div>
    </div>
  </div>
</template>
